import React from "react";
import { Box, Inline, Stack, xcss } from "@atlaskit/primitives";
import Lozenge from "@atlaskit/lozenge";
import Button from "@atlaskit/button";
import { useIntl } from "react-intl";
import { AnnouncementData } from "../../data/announcements";
import { messages } from "./messages";
import { UFOSegment } from "../../analytics/react-ufo";

const AnnouncementCardStyles = xcss({
  backgroundColor: "elevation.surface",
  borderRadius: "radius.large",
  padding: "space.400",
  marginBottom: "space.400",
  boxShadow: "elevation.shadow.raised",
  cursor: "pointer",
  transition: "box-shadow 0.3s ease",
  ":hover": {
    boxShadow: "elevation.shadow.overlay",
  },
});

const InlineStyles = xcss({
  display: "flex",
  gap: "space.100",
  flexWrap: "wrap",
});

const HeadingSmallTextStyles = xcss({
  font: "font.heading.medium",
  fontWeight: "font.weight.medium",
  marginBottom: "space.200",
});

const BodyTextStyles = xcss({
  font: "font.body",
  marginBottom: "space.200",
});

export const MediaStyles: ReturnType<typeof xcss> = xcss({
  maxWidth: "100%",
  height: "auto",
  borderRadius: "radius.small",
  marginBottom: "space.300",
});

const LinkButtonStyles = xcss({
  marginRight: "space.100",
  marginBottom: "space.100",
});

const DateStyles = xcss({
  font: "font.body.small",
  color: "color.text.subtlest",
});

interface AnnouncementCardProps {
  announcement: AnnouncementData;
  onClick: () => void;
}

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({ announcement, onClick }) => {
  const { formatMessage } = useIntl();
  return (
    <Box
      aria-label={formatMessage(messages.announcementAriaLabel, { title: announcement.title })}
      onClick={onClick}
      role="button"
      tabIndex={0}
      xcss={AnnouncementCardStyles}
    >
      <Stack space="space.200">
        <UFOSegment name="announcement-header">
          <Inline spread="space-between">
            <Box color="color.text" xcss={HeadingSmallTextStyles}>
              {announcement.title}
            </Box>
            <Box xcss={InlineStyles}>
              {announcement.tags.map((tag) => (
                <Lozenge key={tag} appearance="new" isBold>
                  {tag.toUpperCase()}
                </Lozenge>
              ))}
            </Box>
          </Inline>
        </UFOSegment>

        {announcement.media && (
          <UFOSegment name="announcement-media">
            <Box alt={announcement.media.alt ?? ""} as="img" src={announcement.media.url.default} xcss={MediaStyles} />
          </UFOSegment>
        )}

        <UFOSegment name="announcement-content">
          <Box xcss={BodyTextStyles}>{announcement.content}</Box>
        </UFOSegment>

        {announcement.links && (
          <UFOSegment name="announcement-links">
            <Box xcss={InlineStyles}>
              {announcement.links.map((link, index) => (
                <Box key={index} xcss={LinkButtonStyles}>
                  <Button appearance="default" href={link.url} rel="noopener noreferrer" target="_blank">
                    {link.text}
                  </Button>
                </Box>
              ))}
            </Box>
          </UFOSegment>
        )}

        <Box xcss={DateStyles}>{announcement.date}</Box>
      </Stack>
    </Box>
  );
};

export default AnnouncementCard;
